import { useState } from "react";
import { AuthImage } from "@/components/AuthImage";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, BookOpen, Calendar, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function SessionFeed({ sessions, emptyText = "No sessions logged yet.", onDelete }) {
  const [lightbox, setLightbox] = useState(null);

  if (!sessions.length) {
    return (
      <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground text-sm">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="parent-session-feed">
      {sessions.map((s) => (
        <div
          key={s.id}
          data-testid="session-card"
          className="rounded-lg border border-border bg-card shadow-sm overflow-hidden"
        >
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-brand text-lg">{s.student_name}</span>
                  <span className="eyebrow text-[9px] text-muted-foreground">· {s.subject}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono-tt">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {s.date}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {s.duration_minutes} min
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> {s.teacher_name}
                  </span>
                </div>
              </div>
              {onDelete && (
                <Button variant="ghost" size="icon" onClick={() => onDelete(s.id)} data-testid="delete-session-button">
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              )}
            </div>

            {s.objectives && (
              <div className="mt-4">
                <div className="eyebrow text-[9px] text-muted-foreground mb-1">Objectives</div>
                <p className="text-sm">{s.objectives}</p>
              </div>
            )}

            <div className="mt-4">
              <div className="eyebrow text-[9px] text-muted-foreground mb-1">Session notes</div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.note}</p>
            </div>

            {s.photo_paths?.length > 0 && (
              <div
                data-testid="session-card-photo-grid"
                className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2"
              >
                {s.photo_paths.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`session-photo-${i}`}
                    onClick={() => setLightbox(p)}
                    className="aspect-square overflow-hidden rounded-md border border-border group"
                  >
                    <AuthImage
                      path={p}
                      alt={`Session work ${i + 1}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black border-zinc-800" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">Session work photo</DialogTitle>
          <DialogDescription className="sr-only">Enlarged view of the uploaded session work photo.</DialogDescription>
          {lightbox && (
            <AuthImage path={lightbox} alt="Session work" className="w-full max-h-[80vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
